import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EMPTY, catchError } from 'rxjs';

import { environment } from '../../../environments/environment';
import { isTestDeployment } from '../helpers/deploy-environment.helper';
import { AuthTokenStore } from './auth-token.store';
import { LogEvent } from './log-events';

/** Syslog severities, as Graylog expects them. Higher integer = more verbose. */
export enum LogLevel {
	error = 3,
	warn = 4,
	info = 6,
}

/** Primitives only. See the PII contract on {@link LoggingService}. */
export type LogFields = Record<string, string | number | boolean | null | undefined>;

/**
 * The body of `POST /customersupport/v1/logmessage`, the bridge that forwards to Graylog.
 *
 * Only the columns this app populates. Everything else rides `keyValuePairs`, which the bridge
 * re-expands into `_`-prefixed GELF custom fields — so a new field here never needs a backend
 * change (customersupport-api EB-3301).
 */
export interface BridgeLogMessage {
	host: string;
	short_message: string;
	level: number;
	project: 'connect';
	_environment: string;
	keyValuePairs: { key: string; value: string }[];
}

/** Values longer than this are cut; a log line is not a transport for payloads. */
const VALUE_MAX = 500;

/**
 * PS-6495 — structured logging for the Connect page, through the customersupport-api bridge.
 *
 * **Why a bridge and not Graylog directly.** Connect is served from a PUBLIC CloudFront origin. A
 * browser POST straight to the internal syslog host would be blocked by the distribution's CSP, and
 * adding that host to `connect-src` would publish an internal hostname and port in a world-readable
 * header. The bridge sits on the normal API host, which the CSP already allows, so the internal
 * host stays server-side. Same reasoning and same transport as AMIE (EB-3798) and EVA.
 *
 * **Deliberately a fraction of AMIE's service.** No rate limiter, no HTTP-error dedup, no verbose
 * toggle, no global `ErrorHandler`, no interceptor of its own. AMIE needs those because it logs from ~15 sites
 * plus every failed request, where one looping view can flood the ingest. This app emits four
 * events in a run that a human clicks through. Porting the machinery would be copying the shape of
 * a solution without its problem.
 *
 * **What it never does:** throw. Logging that can break a feature is worse than no logging, so
 * every path here swallows its own failures.
 *
 * ## The PII contract
 *
 * Values must be primitives, and must not carry personal data: no phone numbers, no names, no
 * tokens, no free-text the customer typed. The number the customer enters is explicitly out
 * (Torsten, 2026-10-09) — the events record THAT one was set and where it came from, not which. A
 * `phoneServiceId` is fine: it identifies an account, it is already in every backend log, and
 * without it an event cannot be tied to anything at all.
 */
@Injectable({
	providedIn: 'root',
})
export class LoggingService {
	/**
	 * The ordinary client, so the POST goes through `authTokenInterceptor`, which attaches the
	 * bearer because `/customersupport/v1/logmessage` is on its allowlist (L7, code review 2) - the
	 * single place a token is sent. No interceptor in this app logs, so there is no recursion to
	 * guard against; one that ever does must skip this path.
	 */
	private readonly http = inject(HttpClient);
	private readonly tokens = inject(AuthTokenStore);

	/** A product-usage event. Always INFO — these describe behaviour, not health. */
	usage(event: LogEvent, fields?: LogFields): void {
		this.emit(LogLevel.info, event, fields);
	}

	/** Something went wrong in a way worth seeing in Graylog. */
	error(event: LogEvent, fields?: LogFields): void {
		this.emit(LogLevel.error, event, fields);
	}

	private emit(level: LogLevel, event: LogEvent, fields?: LogFields): void {
		try {
			const payload = this.build(level, event, fields);

			// Mirrored outside live so a developer sees the event without a deploy, and so a broken
			// pipeline is visible in a support screen-share.
			if (!this.isLive()) {
				console.info(event, payload);
			}

			this.send(payload);
		} catch {
			// A logging bug must not take a customer's order down with it.
		}
	}

	/**
	 * Fire and forget. No retry — a retry during a bridge outage amplifies the outage — and no error
	 * surfaced, because surfacing it is how a logging failure becomes a customer-visible one.
	 */
	private send(payload: BridgeLogMessage): void {
		const token = this.tokens.token;
		if (!token || this.tokens.isExpired) {
			// The bridge requires auth, so a token-less POST would 401 and vanish. Nothing is logged
			// before the sign-in by design, so this is a real anomaly rather than the normal case.
			console.warn('LoggingService: no usable token, event not sent —', payload.short_message);
			return;
		}

		this.http
			.post(`${environment.baseUrl}/customersupport/v1/logmessage`, payload)
			.pipe(
				catchError((error: unknown) => {
					if (!this.isLive()) {
						console.error('LoggingService: bridge POST failed —', payload.short_message, error);
					}
					return EMPTY;
				}),
			)
			.subscribe();
	}

	private build(level: LogLevel, event: LogEvent, fields?: LogFields): BridgeLogMessage {
		const payload: BridgeLogMessage = {
			host: this.host(),
			short_message: event,
			level,
			project: 'connect',
			_environment: this.environmentName(),
			keyValuePairs: [],
		};

		for (const [key, value] of Object.entries(fields ?? {})) {
			// An absent field is absent, not the string "undefined" — which is what a Graylog facet
			// would otherwise fill up with.
			if (value === undefined || value === null) {
				continue;
			}
			payload.keyValuePairs.push({
				key: this.clean(key),
				value: this.cap(this.clean(String(value))),
			});
		}

		return payload;
	}

	/** The ENVIRONMENT, not the build type - see `isTestDeployment`. */
	private environmentName(): string {
		return isTestDeployment() ? 'test' : 'live';
	}

	private isLive(): boolean {
		return this.environmentName() === 'live';
	}

	private host(): string {
		try {
			return window.location.hostname;
		} catch {
			return '';
		}
	}

	/**
	 * Strip every C0 control character (U+0000-U+001F) and DEL, so a value can never forge a second
	 * log line or carry a terminal escape into a log viewer. Until code review 2 only `\n\r\t` went,
	 * while this comment already promised all of them.
	 */
	private clean(value: string): string {
		return value.replace(/[\u0000-\u001f\u007f]/g, '');
	}

	private cap(value: string): string {
		return value.length > VALUE_MAX ? value.slice(0, VALUE_MAX) : value;
	}
}
